/**
 * CrmLayout — persistent shell for /crm/* (mounted once; tabs swap the <Outlet />).
 * Left nav: Home | Leads | Companies | Opportunities | Activity. Top bar reuses the shared
 * Active / Break / Downtime / Notifications controls AS-IS (attendance logic untouched).
 */
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { Home, Users, Building2, Target, ListChecks, LogOut, Shield, ArrowLeft, Gauge, Menu, BarChart3, UserRound } from 'lucide-react';
import { CrmSearch } from './CrmHeaderWidgets';
import { CrmFollowupsDock } from './CrmFollowupsDock';
import TitleFlowLogo from '../TitleFlowLogo';
import NotificationBell from '../shared/NotificationBell';
import { SessionHealthIndicator, BreakButton, SystemDowntimeButton } from '../session';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth } from '../../App';
import { getRoleDisplayName } from '../../lib/dashboardViews';

export const CRM_ROLES = ['sales_marketing', 'admin'];

/** Per-page guard for /admin/crm/* — the AdminLayout shell admits HR/IT/jurisdiction personas, CRM admin pages are Admin-only. */
export const CrmAdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

export const CrmProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!CRM_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const NAV = [
  { to: '/crm', label: 'Home', icon: Home, end: true, testid: 'crm-nav-home' },
  { to: '/crm/leads', label: 'Leads', icon: Users, testid: 'crm-nav-leads' },
  { to: '/crm/companies', label: 'Companies', icon: Building2, testid: 'crm-nav-companies' },
  { to: '/crm/opportunities', label: 'Opportunities', icon: Target, testid: 'crm-nav-opportunities' },
  { to: '/crm/activity', label: 'Activity', icon: ListChecks, testid: 'crm-nav-activity' },
  { to: '/crm/performance', label: 'My Performance', icon: Gauge, testid: 'crm-nav-performance' },
  // SALES-MARKETING-WORKSPACE-001: employee self-service (Leave / IT Support / Policies) inside the CRM shell
  { to: '/crm/workspace', label: 'My Workspace', icon: UserRound, testid: 'crm-nav-workspace' },
];

export const CrmScopeContext = React.createContext({ owner: null, setOwner: () => {} });

export const CrmLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [owner, setOwner] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const initials = (user?.full_name || 'S M').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <CrmScopeContext.Provider value={{ owner, setOwner }}>
      <div className="min-h-screen bg-slate-50 flex" data-testid="crm-layout">
        {menuOpen && <button aria-label="Close menu" onClick={() => setMenuOpen(false)} className="fixed inset-0 bg-black/40 z-40 lg:hidden" data-testid="crm-menu-backdrop" />}
        <aside className={`w-56 shrink-0 bg-[#0f172a] text-slate-200 flex flex-col h-screen overflow-y-auto fixed lg:sticky top-0 z-50 lg:z-auto transition-transform duration-200 ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="crm-sidebar" onClick={() => setMenuOpen(false)}>
          <div className="h-16 flex flex-col justify-center px-4 border-b border-white/10">
            <TitleFlowLogo variant="darkBright" style={{ height: 26, width: 'auto' }} />
            <div className="text-[10px] text-slate-400 mt-0.5 pl-0.5">A PNA Title Company</div>
          </div>
          {user?.role === 'admin' && (
            <div className="px-3 pt-3">
              <button onClick={() => navigate('/admin')} data-testid="crm-back-to-admin" className="crm-control w-full h-9 flex items-center gap-2.5 px-3 rounded-lg border border-white/15 bg-white/[.06] text-slate-200 transition-[background-color,border-color,color] duration-200 hover:bg-indigo-600/30 hover:border-violet-400/60 hover:text-white active:scale-[.99]">
                <ArrowLeft className="h-4 w-4 text-violet-300" /> Back to Admin
              </button>
            </div>
          )}
          <div className="px-4 pt-5 pb-2 text-[10px] font-bold tracking-[.18em] text-slate-400">SALES CRM</div>
          <nav className="flex-1 px-2 space-y-1">
            {NAV.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink key={to} to={to} end={end} data-testid={testid}
                className={({ isActive }) => `crm-nav-item flex items-center transition-[background-color,color,transform] duration-200 active:scale-[.99] ${isActive ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold shadow-lg shadow-violet-900/40' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </nav>
          <div className="mx-3 mb-3 rounded-2xl bg-white/[.06] border border-white/10 p-4 text-slate-200 hidden lg:block" data-testid="sidebar-quote">
            <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center mb-3"><BarChart3 className="h-5 w-5 text-violet-300" /></div>
            <p className="text-sm leading-snug">“More Conversations.<br />More Opportunities.”</p>
          </div>
          {user?.role === 'admin' && (
            <div className="m-2 space-y-0.5 border-t border-white/10 pt-2" data-testid="crm-admin-links">
              <button onClick={() => navigate('/admin/crm')} data-testid="crm-admin-link" className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-slate-300 hover:bg-white/10">
                <Shield className="h-3.5 w-3.5" /> Admin · Team Activity
              </button>
            </div>
          )}
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between gap-3 px-3 sm:px-6 sticky top-0 z-40" data-testid="crm-topbar">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button onClick={() => setMenuOpen(true)} className="lg:hidden h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center" aria-label="Open menu" data-testid="crm-menu-btn"><Menu className="h-5 w-5 text-slate-700" /></button>
              <div className="text-sm font-semibold text-slate-700 hidden xl:block whitespace-nowrap">Sales & Marketing Workspace</div>
              <CrmSearch />
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0 max-w-[60vw] sm:max-w-none overflow-x-auto">
              <SessionHealthIndicator />
              <BreakButton />
              <SystemDowntimeButton />
              <NotificationBell user={user} />
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-full border border-slate-200 hover:bg-slate-50" data-testid="crm-profile-chip">
                    <span className="h-7 w-7 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">{initials}</span>
                    <span className="text-left leading-tight"><span className="block text-xs font-semibold text-slate-800">{user?.full_name}</span><span className="block text-[10px] text-slate-500">{getRoleDisplayName(user?.role)}</span></span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => { logout({ skipRedirect: true }); navigate('/login', { replace: true }); }} className="text-red-600 cursor-pointer" data-testid="crm-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          {/* Follow-ups dock publishes --crm-dock-reserve (its width + gutter) while open → content re-centres beside it and is never covered (xl+, ≥1280px; below that the panel overlays as before) */}
          <main className="min-w-0 xl:pr-[var(--crm-dock-reserve,0px)] transition-[padding] duration-200" data-testid="crm-main">
            <div className="p-4 sm:p-6 pb-20 max-w-[1400px] w-full mx-auto min-w-0"><Outlet /></div>
          </main>
          <CrmFollowupsDock />
        </div>
      </div>
    </CrmScopeContext.Provider>
  );
};

export default CrmLayout;
